import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { environment } from '../../environments/environment';
import
{
  ActiveForms, Client, Form, AvailableForm, ICentralNote, IClientDocument,
  IClientDocumentGroups, IClientDocuments, IConsentType, IContactEvent,
  IContactEventOptions,IDiyaClient,
  IHealthwiseDocument,
  IHealthwiseSession,
  IMonthGroup, ITask, IYearGroup, Timeline
} from '../models/client';
import { NotificationService } from './notification-service';
import { SessionStorageService } from './session-storage.service';
import { Months } from '../models/common';
import { User } from '../models/user';

@Injectable( {
  providedIn: 'root'
} )
export class ClientService
{

  private API_BASE = environment.apiBaseUrl;
  private API_USER = environment.apiEndpoints.user;

  constructor(
    private _http: HttpClient,
    private _notificationService: NotificationService,
    private _storageService: SessionStorageService,
  ) { }

  public async setViewedClient( client: Client ): Promise<void> 
  {
    const viewedClients: Client[] = this._storageService.get<Client[]>( 'viewedClients' ) || [];
    if ( !viewedClients.find( c => c.clientId === client.clientId ) )
    {
      viewedClients.push( client );
      this._storageService.set( 'viewedClients', viewedClients );
    }

  }

  public getClient( clientId: string ): Promise<Client | undefined>
  {
    let client: Promise<Client | undefined> = Promise.resolve(undefined);

    const cacheClient = this._storageService.get<Client[]>( `clients` );
    if ( cacheClient )
    {
      const client = cacheClient.find( c => c.clientId === clientId );
      if ( client )
      {
        return Promise.resolve( client );
      }
    }

    try
    {
      client =  this._http.get<Client>( `${this.API_BASE}${this.API_USER.clients.client}/${clientId}` ).toPromise();
    }
    catch ( error )
    {
      throw error;
    }
    finally {
      return client;
    }
   
  }

  public async getActiveForms( clientId: string ): Promise<ActiveForms | undefined>
  {

    if ( this._storageService.has( `${clientId}_activeForms` ) )
    {
      return Promise.resolve( this._storageService.get<ActiveForms>( `${clientId}_activeForms` ) || undefined );
    }

    const results = await this._http.get<Form[]>( `${this.API_BASE}${this.API_USER.clients.client}/${clientId}/ActiveForms` ).toPromise();

    if ( !results ) return Promise.resolve( undefined );

    const activeForms: ActiveForms = {
      all: results?.reduce( ( acc, form ) =>
      {
        // Group forms by formPct
        const key = form.formPct || 'Other';
        if ( !acc[key] )
        {
          acc[key] = [];
        }
        acc[key].push( form );
        return acc;
      }, {} as Record<string, Form[]> )
    };

    this._storageService.set( `${clientId}_activeForms`, activeForms );

    return Promise.resolve( this._storageService.get<ActiveForms>( `${clientId}_activeForms` ) || undefined );

  }

  public async getArchivedForms( clientId: string ): Promise<ActiveForms | undefined>
  {

    if ( this._storageService.has( `${clientId}_archivedForms` ) )
    {
      return Promise.resolve( this._storageService.get<ActiveForms>( `${clientId}_archivedForms` ) || undefined );
    }

    const results = await this._http.get<Form[]>( `${this.API_BASE}${this.API_USER.clients.client}/${clientId}/ArchivedForms` ).toPromise();

    if ( !results ) return Promise.resolve( undefined );

    const archivedForms: ActiveForms = {
      all: results?.reduce( ( acc, form ) =>
      {
        // Group forms by formPct
        const key = form.formPct || 'Other';
        if ( !acc[key] )
        {
          acc[key] = [];
        }
        acc[key].push( form );
        return acc;
      }, {} as Record<string, Form[]> )
    };

    this._storageService.set( `${clientId}_archivedForms`, archivedForms );

    return Promise.resolve( this._storageService.get<ActiveForms>( `${clientId}_archivedForms` ) || undefined );

  }

  public async getContactEvents( clientId: string ): Promise<IContactEvent[] | undefined>
  {
    if ( this._storageService.has( `${clientId}_contactEvents` ) )
    {
      return this._storageService.get<IContactEvent[]>( `${clientId}_contactEvents` ) || undefined;
    }

    const results = await this._http.get<IContactEvent[]>( `${this.API_BASE}${this.API_USER.clients.contactEvents}/${clientId}` ).toPromise();

    if ( !results ) return undefined;

    this._storageService.set( `${clientId}_contactEvents`, results );

    return this._storageService.get<IContactEvent[]>( `${clientId}_contactEvents` ) || undefined;
  }

  public async getContactEvent( clientId: string, eventId: string ): Promise<IContactEvent | undefined>
  {
    // try to find in cache first
    const cachedEvents = this._storageService.get<IContactEvent[]>( `${clientId}_contactEvents` );
    if ( cachedEvents )
    {
      const cachedEvent = cachedEvents.find( e => e.eventId === eventId );
      if ( cachedEvent )
      {
        return Promise.resolve( cachedEvent );
      }      
    }

    const result = await this._http.get<IContactEvent>( `${this.API_BASE}${this.API_USER.clients.contactEvent}/${eventId}` ).toPromise();
    return result;

  }

  public async getContactEventNotes( eventId: string ): Promise<ICentralNote[] | undefined>
  {
    const url = `${this.API_BASE}${this.API_USER.clients.contactEventNotes}/${eventId}`;
    const results = await this._http.get<ICentralNote[]>( url ).toPromise();
    return results;
  }

  public async getContactEventOptions( clientId: string ): Promise<IContactEventOptions | undefined>
  {
    if ( this._storageService.has( `${clientId}_contactEventOptions` ) )
    {
      return Promise.resolve( this._storageService.get<IContactEventOptions>( `${clientId}_contactEventOptions` ) || undefined);
    }
    const url = `${this.API_BASE}${this.API_USER.clients.contactEventOptions}?clientId=${clientId}`;
    const results = await this._http.get<IContactEventOptions>( url ).toPromise();

    if ( !results ) return Promise.resolve( undefined );

    this._storageService.set( `${clientId}_contactEventOptions`, results );

    return Promise.resolve( this._storageService.get<IContactEventOptions>( `${clientId}_contactEventOptions` ) || undefined );

  }

  public async getTasks( clientId: string ): Promise<ITask[] | undefined>
  {
    if ( this._storageService.has( `${clientId}_tasks` ) )
    {
      return Promise.resolve( this._storageService.get<ITask[]>( `${clientId}_tasks` ) || undefined );
    }

    const results = await this._http.get<ITask[]>( `${this.API_BASE}${this.API_USER.clients.client}/${clientId}/Tasks` ).toPromise();

    if ( !results ) return Promise.resolve( undefined );

    this._storageService.set( `${clientId}_tasks`, results );

    return Promise.resolve( this._storageService.get<ITask[]>( `${clientId}_tasks` ) || undefined );
  }

  public async getCentralNotes( clientId: string ): Promise<ICentralNote[] | undefined>
  {
    if ( this._storageService.has( `${clientId}_centralnotes` ) )
    {
      return Promise.resolve( this._storageService.get<ICentralNote[]>( `${clientId}_centralnotes` ) || undefined );
    }

    const results = await this._http.get<ICentralNote[]>( `${this.API_BASE}${this.API_USER.clients.CentralNotes}/${clientId}` ).toPromise();

    if ( !results ) return Promise.resolve( undefined );

    this._storageService.set( `${clientId}_centralnotes`, results );

    return Promise.resolve( this._storageService.get<ICentralNote[]>( `${clientId}_centralnotes` ) || undefined );
  }

  public async getDocuments( clientId: string ): Promise<IClientDocuments | undefined>
  {
    if ( this._storageService.has( `${clientId}_documents` ) )
    {
      return this._storageService.get<IClientDocuments>( `${clientId}_documents` ) || undefined;
    }

    const results = await this._http.get<ICentralNote[]>( `${this.API_BASE}${this.API_USER.clients.documents}/${clientId}` ).toPromise();

    if ( !results ) return undefined;

    this._storageService.set( `${clientId}_documents`, results );

    return this._storageService.get<IClientDocuments>( `${clientId}_documents` ) || undefined;
  }

  public async getGroupedDocuments( documents: IClientDocument[] ): Promise<IClientDocumentGroups>
  {
    // helper function to get month name
    const getMonthName = ( date: Date ) => date.toLocaleDateString( 'en-US', { month: 'long' } );

    const groupDocuments = ( documents: IClientDocument[] ): IClientDocumentGroups =>
    {
      // use temporary map to build the structure
      const tempGroups: {
        [year: number]: { [month: string]: IClientDocument[] }
      } = {};

      documents.forEach( doc =>
      {
        const date = new Date( doc.documentDate );
        const year = date.getFullYear();
        const month = getMonthName( date );

        if ( !tempGroups[year] )
        {
          tempGroups[year] = {};
        }
        if ( !tempGroups[year][month] )
        {
          tempGroups[year][month] = [];
        }
        tempGroups[year][month].push( doc );

      } );

      // transform
      const years: IYearGroup[] = [];
      for ( const year in tempGroups )
      {
        const months: IMonthGroup[] = [];
        for ( const month in tempGroups[year] )
        {
          months.push( { month, documents: tempGroups[year][month] } );
        }
        years.push( { year: Number( year ), months } )
      }

      //sort
      years.sort( ( a, b ) => b.year - a.year );
      years.forEach( y =>
      {
        y.months.sort( ( a, b ) =>
        {
          const monthOrder = Months;
          return monthOrder.indexOf( b.month ) - monthOrder.indexOf( a.month );
        } );

        y.months.forEach( month =>
        {
          month.documents.sort( ( a, b ) => new Date( b.uploadDate ).getTime() - new Date( a.uploadDate ).getTime() );
        } );

      } );

      // return
      return { years };
    }

    return groupDocuments( documents );
  }

  public async uploadDocuments( clientId: string, month: number, year: number, displayName: string, files: File[], tag?: string ): Promise<void>
  {
    // remove cache documents
    this._storageService.delete( `${clientId}_documents` );

    // helper to extract file extension
    const getFileExtension = async ( file: File ): Promise<string | undefined> =>
    {
      let extension: string | undefined;

      const parts = file.name.toLowerCase().split( '.' );
      extension = parts[parts.length - 1];
      return extension;
    }

    // prepare upload form data
    let date = new Date( year, month, 2 );
    let tagParam = tag ? `tag=${tag}` : '';



    files.forEach( async ( file ) =>
    {
      const form = new FormData();
      const fullname = `${displayName}.${await getFileExtension( file )}`;
      form.append( "File", file );
      form.append( "ClientId", clientId );
      form.append( "DocumentDate", date.toISOString() );
      form.append( "DisplayName", fullname );
      form.append( "Tag", tagParam );

      const url = `${this.API_BASE}${this.API_USER.clients.uploadDocument}`;

      const result = await this._http.post( url, form, { responseType: 'text' } ).toPromise();
      this._notificationService.showSuccessMessage( `${result}: ${fullname}` || `${fullname} successfully uploaded` );

    } );
  }

  public async deleteDocument( clientId: string, documentId: number ): Promise<void>
  {

    // remove cache documents
    this._storageService.delete( `${clientId}_documents` );

    // send remove call to api
    const url = `${this.API_BASE}${this.API_USER.clients.deleteDocument}/${documentId}`;
    const result = await this._http.delete<string>( url ).toPromise();
    this._notificationService.showSuccessMessage( result || 'Document succesfully deleted.' );

  }

  public async downloadDocument( documentId: number ): Promise<string | undefined>
  {
    const token = await this.getDocumentViewToken();
    const url = `${this.API_BASE}${this.API_USER.clients.dowloadDocument}/${documentId}?viewToken=${token}`;

    return url;
  }

  public async getDocumentViewToken(): Promise<string | undefined>
  {
    const url = `${this.API_BASE}${this.API_USER.clients.viewToken}`;
    const result = await this._http.get<string>( url ).toPromise();

    return result;
  }

  public async getTimeline( clientId: string ): Promise<Timeline | undefined>
  {
    if ( this._storageService.has( `${clientId}_timeline` ) )
    {
      return this._storageService.get( `${clientId}_timeline` ) as Timeline;
    }
    const url = `${this.API_BASE}${this.API_USER.clients.client}/${clientId}/${this.API_USER.clients.timeline}`;
    const result = await this._http.get<Timeline>( url ).toPromise();

    if ( result )
    {
      this._storageService.set( `${clientId}_timeline`, result );
    }

    return this._storageService.get( `${clientId}_timeline` ) as Timeline;
  }

  public async getHealthwiseDocuments( clientId: string ): Promise<IHealthwiseDocument[] | undefined>
  {
    const url = `${this.API_BASE}${this.API_USER.clients.healthwise.activity}?clientId=${clientId}`;
    const result = await this._http.get<IHealthwiseDocument[]>( url ).toPromise();

    return result;
  }

  public async getHealthwiseLaunchUrl( clientId: string ): Promise<IHealthwiseSession | undefined>
  {
    if ( this._storageService.has( `${clientId}_hwlurl` ) )
    {
      return this._storageService.get( `${clientId}_hwlurl` ) || undefined;
    }

    const url = `${this.API_BASE}${this.API_USER.clients.healthwise.launchUrl}?clientId=${clientId}`;
    const result = await this._http.post<IHealthwiseSession>( url, null ).toPromise();
    if ( result )
    {
      this._storageService.set( `${clientId}_hwlurl`, result );
    }

    return this._storageService.get( `${clientId}_hwlurl` ) || undefined;
  }

  public async printHealthwiseDocument( documentId: number ): Promise<File | undefined>
  {
    const url = `${this.API_BASE}${this.API_USER.clients.healthwise.print}/${documentId}`;
    const result = await this._http.get( url, { responseType: 'arraybuffer' } ).toPromise();

    if ( !result ) return undefined;
    const blob = new Blob( [result], { type: 'application/pdf' } );
    const document = new File( [blob], `${documentId}.pdf`, { type: blob.type } )
    return document;
  }

  public async endHealthwiseSession( clientId: string, sessionId: string ): Promise<void>
  {
    const url = `${this.API_BASE}${this.API_USER.clients.healthwise.endCallUrl}/${clientId}/${sessionId}`;
    const result = await this._http.post<string>( url, null ).toPromise();
    this._notificationService.showSuccessMessage( 'Session succesfully ended.' );
  }

  public async getDiyaConsentTypes( clientId: string ): Promise<IConsentType[] | undefined>
  {
    const url = `${this.API_BASE}${this.API_USER.clients.diyahealth.client}/${clientId}/consent_types`;
    try
    {
      const result = await this._http.get<any>( url ).toPromise();
      if ( result && result.data === null )
      {
        this._notificationService.showWarningMessage( `No Diya Health consent types found for client ID ${clientId}` );
      }
      return result.data as IConsentType[];
    }
    catch ( error: any )
    {
      const errors = error?.error?.errors;
      this._notificationService.showErrorMessages( errors );
      return undefined;
    }

  }

  public async getDiyaClient( clientId: string ): Promise<IDiyaClient | undefined | null>
  {
    const url = `${this.API_BASE}${this.API_USER.clients.diyahealth.client}/${clientId}`;
    try
    {
      const result = await this._http.get<any>( url ).toPromise();

      if ( result && result.data === null )
      {
        this._notificationService.showWarningMessage( `No Diya Health client found for client ID ${clientId}` );
      }

      return result.data as IDiyaClient;
    }
    catch ( error: any )
    {
      const errors = error?.error?.errors;
      this._notificationService.showErrorMessages( errors );
      return undefined;
    }

  }

  public async getDiyaFile( clientId: string, documentId: string ): Promise<File | undefined>
  {
    const url = `${this.API_BASE}${this.API_USER.clients.diyahealth.client}/${clientId}/document/${documentId}`;
    const result = await this._http.get( url, { responseType: 'arraybuffer' } ).toPromise();
    if ( !result ) return undefined;
    const blob = new Blob( [result], { type: 'application/pdf' } );
    const document = new File( [blob], `${documentId}.pdf`, { type: blob.type } )
    return document;
  }

  public async createDiyaClient( clientId: string ): Promise<IDiyaClient>
  {
    const url = `${this.API_BASE}${this.API_USER.clients.diyahealth.client}/${clientId}`;
    const result = await this._http.post<any>( url, null ).toPromise();

    this._notificationService.showSuccessMessage( result?.messages || 'Diya Health client successfully created.' );

    return result.data as IDiyaClient;
  }

  public async createDiyaConsent( clientId: string, consentTypeId: string ): Promise<void>
  {
    const url = `${this.API_BASE}${this.API_USER.clients.diyahealth.client}/${clientId}/consent`;
    const body = { contentItemId: consentTypeId, isHighPriority: true };
    const result = await this._http.post<any>( url, body ).toPromise();

    this._notificationService.showSuccessMessage( result?.message || 'Consent successfully sent to the client.' );
  }

  public async getAvailableForms( clientId: string ): Promise<AvailableForm[] | undefined> 
  {
    const url = `${this.API_BASE}${this.API_USER.clients.client}/${clientId}/AvailablePctForms`;
    const result = await this._http.get<AvailableForm[]>( url ).toPromise();

    return result;

  }

  public async getVisitbleTabs( clientId: string ): Promise<string[] | undefined>
  {
    const user = this._storageService.get<User>( 'user' );

    const url = `${this.API_BASE}${this.API_USER.tabs}/${user?.userId}/${clientId}/`;
    const result = await this._http.get<string[]>( url ).toPromise();

    return result;

  }

}

export interface TableColumn
{
  label: string;
  field?: string;
  type?: string;
  link?: string;
  format?: string;
  sortable?: boolean;
  filterable?: boolean;
  filterType?: string;
}

export interface FormTableColumns
{
  DefaultColumns: TableColumn[];
  HiddenColumns?: TableColumn[];
}

export const FormTypeTableColumns: Record<string, FormTableColumns> = {
  P: {
    DefaultColumns: [
      { label: 'Pathway', field: 'formName', type: 'link', sortable: true, filterable: true, filterType: 'select' },
      { label: 'Start Date', field: 'startDate', type: 'date', sortable: true },
      { label: 'Service', field: 'formSubType', sortable: true },
      { label: 'Name' },
      { label: 'Steps' },
      { label: 'Last Updated', field: 'lastUpdatedDate', type: 'date', sortable: true },
      { label: 'Last Signed', field: 'lastSignedDate', type: 'date', sortable: true },
    ]
  },
  C: {
    DefaultColumns: [
      { label: 'Checklist', field: 'formName', type: 'link', sortable: true },
      { label: 'Visit Date', field: 'startDate', type: 'date', sortable: true },
      { label: 'Next Visit Date' },
      { label: 'Completion' },
      { label: 'Created Date', field: 'createdDate', type: 'date', sortable: true },
      { label: 'Updated Date', field: 'lastUpdatedDate', type: 'date', sortable: true },
      { label: 'Last Signed', field: 'lastSignedDate', type: 'date', sortable: true },
    ]
  },
  T: {
    DefaultColumns: [
      { label: 'Tool', field: 'formName', type: 'link', sortable: true },
      { label: 'Start Date', field: 'startDate', type: 'date', sortable: true },
      { label: 'Last Signed', field: 'lastSignedDate', type: 'date', sortable: true },
      { label: 'Status' },
      { label: 'Score' },
      { label: 'Call Reason' },
      { label: 'Referral Source' },
    ]
  },
  'T-HC': {
    DefaultColumns: [
      { label: 'Healthy Change Tool', field: 'formName', type: 'link', sortable: true },
      { label: 'Start Date', field: 'startDate', type: 'date', sortable: true },
      { label: 'Last Signed', field: 'lastSignedDate', type: 'date', sortable: true },
      { label: 'Status' },
      { label: 'Score' },
      { label: 'Call Reason' },
      { label: 'Referral Source' },
    ]
  },
  Contact: {
    DefaultColumns: [
      { label: 'Day', field: 'creationDate', type: 'date', format: 'EEE, MMM dd yyyy', sortable: true },
      { label: 'Start', field: 'startTimeString', sortable: true },
      { label: 'End', field: 'endTimeString', sortable: true },
      { label: 'Durantion', field: 'totalTimeString', sortable: true },
      { label: 'Type', field: 'visitType', sortable: true },
      { label: 'Reason', field: 'contactReason' },
      { label: 'Goal', field: 'goal' },
      { label: 'Outcome', field: 'outcome', sortable: true },
      { label: 'Created by', field: 'recordCreatedUserName', sortable: true },
      { label: '', field: 'eventId', type:'link', link:'client/contact/'}

    ]
  },
  Tasks: {
    DefaultColumns: [
      { label: 'Task', field: 'taskText', sortable: true },
      { label: 'Priority', field: 'taskLabel', sortable: true },
      { label: 'Form', field: 'formName', type: 'link', link: '', sortable: true },
      { label: 'Start Date', field: 'insertedDate', type: 'date', sortable: true },
    ]
  },
}
